import React from "react";
import { HighScoreBoard } from "components/HighScoreBoard";
import {
  BoardEntry,
  NameStatus,
  getBoard,
  lastName,
  nameStatus,
  submitScore,
} from "services/highScores";

interface HighScoreEntryProps {
  score: number;
  moveLog: string;
  open: boolean;
  onNewGame: () => void;
  onClose: () => void;
}

// Guidance text shown under the name field for each name_status result.
const STATUS_MESSAGE: Record<NameStatus, string> = {
  available: "Name is available.",
  owned_by_you: "Welcome back — this name is yours.",
  needs_password: "This name is protected — enter its password.",
  taken: "That name is taken. Pick another.",
  not_allowed: "That name isn't allowed. Pick another.",
};

export const HighScoreEntry = ({
  score,
  moveLog,
  open,
  onNewGame,
  onClose,
}: HighScoreEntryProps): JSX.Element | null => {
  const [name, setName] = React.useState<string>(lastName);
  const [password, setPassword] = React.useState<string>("");
  const [status, setStatus] = React.useState<NameStatus | null>(null);
  const [submitting, setSubmitting] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string>("");
  const [submitted, setSubmitted] = React.useState<boolean>(false);
  const [allTime, setAllTime] = React.useState<BoardEntry[]>([]);
  const [weekly, setWeekly] = React.useState<BoardEntry[]>([]);

  // Debounced name check as the player edits the name.
  React.useEffect(() => {
    const trimmed = name.trim();
    if (!trimmed) {
      setStatus(null);
      return;
    }
    let cancelled = false;
    const id = setTimeout(async () => {
      const s = await nameStatus(trimmed);
      if (!cancelled) setStatus(s);
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(id);
    };
  }, [name]);

  // Show the password field only where it does something: optional on a fresh
  // claim (to set one), required on a protected name from another device (to
  // prove ownership). When the name is already yours on this device
  // (owned_by_you), the token authenticates and any password is ignored, so we
  // hide it.
  const showPassword = status === "needs_password" || status === "available";
  const passwordRequired = status === "needs_password";
  const canSubmit =
    !!name.trim() &&
    !submitting &&
    status !== "taken" &&
    status !== "not_allowed" &&
    (!passwordRequired || !!password);

  const loadBoards = async () => {
    const [at, wk] = await Promise.all([
      getBoard("all_time", 50),
      getBoard("weekly", 50),
    ]);
    setAllTime(at);
    setWeekly(wk);
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError("");
    const result = await submitScore(
      name.trim(),
      score,
      moveLog,
      showPassword && password ? password : undefined
    );
    if (result.ok) {
      setSubmitted(true);
      await loadBoards();
    } else if (result.status === 429) {
      // Rate limited — brief back-off then one retry.
      setError("Too many requests. Retrying…");
      setTimeout(handleSubmit, 2000);
      return;
    } else {
      setError(result.error ?? "Could not submit score. Try again.");
    }
    setSubmitting(false);
  };

  // Hidden (but still mounted, so name/submit state survives) until reopened.
  if (!open) return null;

  if (submitted) {
    return (
      <div className="hs-modal-overlay">
        <div className="hs-modal hs-entry" role="dialog" aria-modal="true">
          <button
            className="hs-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
          <h3 className="hs-modal-title">Game Over</h3>
          <h4 className="hs-final-score">Final Score: {score}</h4>
          <div className="hs-saved">Saved as {name.trim()}.</div>
          <HighScoreBoard
            title="All-time"
            entries={allTime}
            highlightName={name.trim()}
            displayLimit={20}
          />
          <HighScoreBoard
            title="This week"
            entries={weekly}
            highlightName={name.trim()}
            displayLimit={20}
          />
          <div className="hs-legend">* Maximum three spots per player.</div>
          <div className="hs-actions">
            <button className="hs-button" onClick={onNewGame}>
              New Game
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="hs-modal-overlay">
      <div className="hs-modal hs-entry" role="dialog" aria-modal="true">
        <button className="hs-modal-close" onClick={onClose} aria-label="Close">
          ×
        </button>
        <h3 className="hs-modal-title">Game Over</h3>
        <h4 className="hs-final-score">Final Score: {score}</h4>
        <div className="hs-field">
          <label className="hs-label" htmlFor="hs-name">
            Enter your name for the high-score board:
          </label>
          <input
            id="hs-name"
            className="hs-input"
            type="text"
            maxLength={20}
            value={name}
            autoComplete="off"
            onChange={(e) => setName(e.target.value)}
            placeholder="Your name"
          />
          {status && (
            <div className={`hs-status hs-status-${status}`}>
              {STATUS_MESSAGE[status]}
            </div>
          )}
        </div>

        {showPassword && (
          <div className="hs-field">
            <label className="hs-label" htmlFor="hs-password">
              {passwordRequired ? "Password" : "Password (optional)"}
            </label>
            <input
              id="hs-password"
              className="hs-input"
              type="password"
              value={password}
              autoComplete="off"
              onChange={(e) => setPassword(e.target.value)}
              placeholder={passwordRequired ? "Required" : "Optional"}
            />
            {!passwordRequired && (
              <div className="hs-help">
                Optional. Only needed if you want to play as this name on a
                different device — set a password so you can claim it there.
              </div>
            )}
          </div>
        )}

        {error && <div className="hs-error">{error}</div>}

        <div className="hs-actions">
          <button
            className="hs-button"
            onClick={handleSubmit}
            disabled={!canSubmit}
          >
            {submitting ? "Submitting…" : "Submit score"}
          </button>
          <button className="hs-button hs-button-secondary" onClick={onNewGame}>
            Skip / New Game
          </button>
        </div>
      </div>
    </div>
  );
};
